import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getProjects, archiveProject, restoreProject,
  getAccountingCostsOnlyJobs, archiveCostsOnlyJob, restoreCostsOnlyJob,
  getArSyncAging, getArSyncCollectionPeriods, getArSyncCollections, getInvoiceHistory, getBillingsProjection,
} from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { Icon, IconBtn, Menu, SegmentedControl, StateScreen } from '../components/ui';
import { billedOf, contractYearOf, NO_CONTRACT_YEAR } from '../components/dashboard/projectUtils';
import { formatMoney, formatMoneyCompact } from '../lib/format';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { outstandingOf, arOutTotals, arAgingBuckets, avgDaysToPay, daysBetween } from '../lib/arTotals';
import { todayCentral, periodKey, dateOf } from '../lib/invoiceHistory';
import { can } from '../permissions';

/* ═══════════════════════════════════════════════════════════════════════════
   Accounting dashboard (Duncan 2026-10-20: "The Accounting dashboard should look different than a
   project manager's dashboard"). Leads with what accounting asked for:
     · Money to collect -- AR / Collections' Outstanding figures (lib/arTotals, the same rules)
     · Billing this month -- what CE invoiced this month (Billing by Phase's invoices) against the
       Billings Projection's projected and pending
     · every job: D and L jobs under drop-downs by CONTRACT year (a job with no project page: the year
       CE first invoiced it), then a drop-down each for P, PDA / PSA (ACTIVE D jobs with no project
       page), E and M -- newest year first. Contract · Billed · Collected · Left to bill.
   A job with a project page opens it; one without opens its Job Costs view. Archive and restore
   (Archive & Restore Jobs) work on both kinds; restore lives on a dashboard's Archived tab (#71).
   ═══════════════════════════════════════════════════════════════════════════ */

const letterOf = (job) => ((/-([A-Z])-\d+\s*$/i.exec(String(job || '')) || [])[1] || '').toUpperCase();
const TYPE_TONE = { D: 'var(--type-labor)', L: 'var(--type-material)', M: 'var(--type-procurement)', P: 'var(--type-milestone)', E: 'var(--type-engineering)' };
const TYPE_RANK = { D: 0, L: 1 };
// The drop-downs after the years, in Duncan's order: P, PDA / PSA, E, M (then anything else).
const TYPE_GROUPS = [
  { key: 'P', label: 'P jobs', note: 'Performance Management' },
  { key: 'PDA', label: 'PDA / PSA', note: 'D jobs with no project page' },
  { key: 'E', label: 'E jobs', note: 'Engineering' },
  { key: 'M', label: 'M jobs', note: 'Material only' },
  { key: 'OTHER', label: 'Other jobs', note: '' },
];
// A D job with no project page is a PDA / PSA while it's active; archived, it files under its year with
// the other finished D and L jobs (most archived ones are construction jobs that never had a page).
const groupOf = (row) => {
  if (row.type === 'P' || row.type === 'E' || row.type === 'M') return row.type;
  if (row.type === 'D' && !row.hasPage && !row.archived) return 'PDA';
  if (row.type === 'D' || row.type === 'L' || (!row.type && row.hasPage)) return 'YEAR';
  return 'OTHER';
};
const money = (n) => (n == null ? '—' : n < -0.004 ? `−${formatMoney(-n)}` : formatMoney(n));
const COLS = '26px minmax(0, 2.3fr) repeat(4, minmax(0, 1fr)) 30px';

const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const label = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' };
const bigNum = { fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 };
const subText = { fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' };

const TypeTag = ({ type }) => {
  const tone = TYPE_TONE[type] || 'var(--text-3)';
  return (
    <span className="mono" title={type ? `${type} job` : 'Job'} style={{ width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: tone, background: `color-mix(in oklch, ${tone} 15%, var(--bg-surface))`, border: `1px solid color-mix(in oklch, ${tone} 25%, transparent)` }}>
      {type || '·'}
    </span>
  );
};

/* One of the two cards across the top: two figures, a bar, and the line under it. */
const SummaryCard = ({ title, left, right, bar, foot, empty }) => (
  <section style={{ ...card, padding: '14px 16px', minWidth: 0 }} aria-label={title}>
    <div style={label}>{title}</div>
    {empty ? (
      <div style={{ ...subText, marginTop: 10 }}>{empty}</div>
    ) : (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, marginTop: 8, flexWrap: 'wrap' }}>
          {[left, right].filter(Boolean).map((f) => (
            <div key={f.sub} style={{ minWidth: 0 }}>
              <div className="mono" style={{ ...bigNum, color: f.color || 'var(--text-1)' }}>{f.value}</div>
              <div className="mono" style={subText}>{f.sub}</div>
            </div>
          ))}
        </div>
        {bar && (
          <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--bg-surface-2)', margin: '12px 0 8px' }}>
            {bar.map((b) => <span key={b.key} title={b.title} style={{ width: `${Math.max(0, Math.min(100, b.pct))}%`, background: b.tone }} />)}
          </div>
        )}
        <div className="mono" style={{ ...subText, lineHeight: 1.5 }}>{foot}</div>
      </>
    )}
  </section>
);

/* A drop-down: the header opens and closes its jobs (the Dashboard's archive-year look) and, closed or
   open, carries the group's totals under the columns (Duncan 2026-10-20: rolled up, they were blank). */
const sumOf = (rows, k) => {
  const known = rows.filter((r) => r[k] != null);
  return known.length ? known.reduce((t, r) => t + r[k], 0) : null;
};
const JobGroup = ({ title, note, rows, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const left = sumOf(rows, 'left');
  return (
    <div style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 11, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '11px 12px', cursor: 'pointer', background: 'transparent', border: 'none', font: 'inherit' }}>
        <span style={{ display: 'inline-flex', justifyContent: 'center', color: 'var(--text-3)', transition: 'transform 180ms ease', transform: open ? 'none' : 'rotate(180deg)' }}><Icon name="chevron-up" size={16} /></span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{title}</span>
          <span className="mono" style={{ minWidth: 20, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>{rows.length}</span>
          {note && <span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note}</span>}
        </span>
        <span className="mono acct-num acct-total">{money(sumOf(rows, 'contract'))}</span>
        <span className="mono acct-num acct-total">{money(sumOf(rows, 'billed'))}</span>
        <span className="mono acct-num acct-total">{money(sumOf(rows, 'collected'))}</span>
        <span className="mono acct-num acct-total" style={{ color: left != null && left < -0.004 ? 'var(--danger)' : undefined }}>{money(left)}</span>
        <span />
      </button>
      {open && <div style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)' }}>{children}</div>}
    </div>
  );
};

const JobRow = ({ row, onOpen, menuItems, busy }) => (
  <div className="acct-row" role="button" tabIndex={0} aria-label={`Open ${row.name}`}
    onClick={() => onOpen(row)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(row); }}
    style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, alignItems: 'center', padding: '9px 12px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>
    <TypeTag type={row.type} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>#{row.job}</span>
        {!row.hasPage && <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', padding: '0 6px', borderRadius: 999, border: '1px solid var(--border-default)', whiteSpace: 'nowrap' }}>Job Costs only</span>}
      </div>
    </div>
    <span className="mono acct-num" style={{ color: row.contract == null ? 'var(--text-4)' : undefined }}>{row.contract == null ? (row.contractSettable ? 'not set' : '—') : money(row.contract)}</span>
    <span className="mono acct-num">{money(row.billed)}</span>
    <span className="mono acct-num">{money(row.collected)}</span>
    <span className="mono acct-num" style={{ color: row.left == null ? 'var(--text-4)' : row.left < -0.004 ? 'var(--danger)' : undefined }}>{money(row.left)}</span>
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
      {menuItems.length > 0 && (
        <Menu trigger={(toggle) => <IconBtn name="kebab" label="More actions" onClick={(e) => { e.stopPropagation(); toggle(e); }} />} items={menuItems} />
      )}
    </span>
  </div>
);

const AccountingDashboard = ({ currentUser, onSelectProject }) => {
  const navigate = useNavigate();
  const { confirm, confirmDialogProps } = useConfirm();
  const canArchive = can(currentUser, 'jobs.archive');
  const [projects, setProjects] = useState(null);
  const [costsOnly, setCostsOnly] = useState(null);
  const [ar, setAr] = useState(undefined); // undefined = loading, null = unavailable
  const [billing, setBilling] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState('active');
  const search = usePageSearch('Search jobs…');

  const loadJobs = useCallback(async () => {
    const [p, c] = await Promise.all([
      getProjects({ exclude_status: 'Pre-Construction' }).catch(() => null),
      getAccountingCostsOnlyJobs().catch(() => null),
    ]);
    setProjects(Array.isArray(p) ? p : (p && p.projects) || []);
    setCostsOnly((c && c.jobs) || []);
    if (!p || !c) setError('Some jobs couldn’t be loaded. Refresh to try again.');
  }, []);

  useEffect(() => {
    loadJobs();
    // Money to collect: the latest AR snapshot's collectible invoices, and the latest month's payments.
    (async () => {
      try {
        const [aging, periods] = await Promise.all([getArSyncAging(), getArSyncCollectionPeriods().catch(() => [])]);
        const rows = outstandingOf(aging).open; // net of credit memos, like AR / Collections
        const latest = (periods || [])[0];
        const paid = latest ? await getArSyncCollections(latest.period).catch(() => []) : [];
        const legs = (paid || []).filter((r) => r.job_id).map((r) => ({ days_to_pay: daysBetween(r.invoice_date, r.payment_date) }));
        setAr({ totals: arOutTotals(rows), buckets: arAgingBuckets(rows), avgDays: avgDaysToPay(legs), asOf: aging && aging[0] ? aging[0].report_date : null });
      } catch (e) { setAr(null); }
    })();
    // Billing this month: CE's invoices dated this month (Central) vs the Billings Projection.
    (async () => {
      const ym = periodKey(todayCentral(), 'month');
      const [hist, proj] = await Promise.all([getInvoiceHistory().catch(() => null), getBillingsProjection().catch(() => null)]);
      if (!hist && !proj) { setBilling(null); return; }
      let billed = 0; let invoices = 0;
      const billedJobs = new Set();
      ((hist && hist.jobs) || []).forEach((j) => (j.invoices || []).forEach((inv) => {
        if (periodKey(dateOf(inv), 'month') !== ym) return;
        billed += Number(inv.amount) || 0;
        if ((Number(inv.amount) || 0) > 0) { invoices += 1; billedJobs.add(String(j.job || '').toUpperCase()); }
      }));
      const projected = proj ? Number((proj.monthlyForecast || {})[ym]) || 0 : null;
      const pending = proj ? Number((proj.monthlyPending || {})[ym]) || 0 : null;
      const notBilled = proj ? (proj.byProject || []).filter((p) => (Number((p.monthly || {})[ym]) || 0) > 0
        && !billedJobs.has(String(p.number || '').trim().toUpperCase())).length : null;
      setBilling({ ym, billed: hist ? billed : null, invoices, projected, pending, notBilled });
    })();
  }, [loadJobs]);

  // Every job as one row: a project (it has a page), or a job that lives only on Job Costs.
  const rows = useMemo(() => {
    if (!projects || !costsOnly) return null;
    const out = [];
    projects.forEach((p) => {
      const type = letterOf(p.project_number);
      if (type === 'W') return; // Job Costs → Warranty's
      const contract = parseFloat(p.revised_contract_value || p.contract_amount || 0) || null;
      const billed = billedOf(p);
      const collected = p.billing ? Number(p.billing.collected) || 0 : Number(p.collected_to_date) || 0;
      // Contract year; a project with no contract date yet files under its start year (Duncan 2026-10-20:
      // the dates come from the Active Job Summary; "Jobs the workbook doesn't have fall back to their start date").
      const cy = contractYearOf(p);
      const sy = /^(\d{4})-\d{2}/.exec(String(p.start_date || ''));
      const year = cy !== NO_CONTRACT_YEAR ? Number(cy) : sy ? Number(sy[1]) : null;
      out.push({ key: `p${p.id}`, projectId: p.id, slug: p.slug, job: p.project_number || '', name: p.project_name || p.project_number, type, hasPage: true,
        archived: !!p.archived_at, year, contract, billed, collected, left: contract ? contract - billed : null });
    });
    costsOnly.forEach((j) => {
      const contract = j.contract && j.contract.value != null ? j.contract.value : null;
      out.push({ key: `j${j.job}`, job: j.job, name: j.name, type: j.type, hasPage: false, archived: j.archived, year: j.year,
        contract, contractSettable: !!j.contract, billed: j.billed, collected: j.collected, left: contract ? contract - j.billed : null });
    });
    return out;
  }, [projects, costsOnly]);

  const shown = useMemo(() => (rows || []).filter((r) => (tab === 'archived') === r.archived
    && matchesSearch(search, r.name, r.job)), [rows, tab, search]);
  const counts = useMemo(() => ({
    active: (rows || []).filter((r) => !r.archived).length,
    archived: (rows || []).filter((r) => r.archived).length,
  }), [rows]);

  // D and L jobs by contract year (newest first, no date last; D before L, then number); then P,
  // PDA / PSA, E, M -- each newest year first, then job number.
  const groups = useMemo(() => {
    const years = new Map();
    const types = new Map(TYPE_GROUPS.map((g) => [g.key, []]));
    shown.forEach((r) => {
      const g = groupOf(r);
      if (g === 'YEAR') {
        const k = r.year || NO_CONTRACT_YEAR;
        if (!years.has(k)) years.set(k, []);
        years.get(k).push(r);
      } else types.get(g).push(r);
    });
    const byNumber = (a, b) => a.job.localeCompare(b.job);
    const yearGroups = [...years.entries()]
      .sort(([a], [b]) => (a === NO_CONTRACT_YEAR) - (b === NO_CONTRACT_YEAR) || Number(b) - Number(a))
      .map(([k, list]) => ({ key: `y${k}`, title: String(k), note: '', rows: list.sort((a, b) => ((TYPE_RANK[a.type] ?? 2) - (TYPE_RANK[b.type] ?? 2)) || byNumber(a, b)) }));
    const typeGroups = TYPE_GROUPS.filter((g) => types.get(g.key).length).map((g) => ({
      key: g.key, title: g.label, note: g.note,
      rows: types.get(g.key).sort((a, b) => ((b.year || 0) - (a.year || 0)) || byNumber(a, b)),
    }));
    return [...yearGroups, ...typeGroups];
  }, [shown]);

  const openRow = (row) => {
    if (row.hasPage) onSelectProject(row.slug || row.projectId);
    else navigate(`/job-costs?job=${encodeURIComponent(row.job)}`);
  };
  const act = async (row, kind) => {
    if (busy) return;
    if (kind === 'archive') {
      const ok = await confirm({
        title: `Archive ${row.name}?`,
        message: 'It leaves the active lists and its CE cost pulls stop. Everything is kept, and you can restore it from the Archived tab here.',
        confirmLabel: 'Archive job',
      });
      if (!ok) return;
    }
    setBusy(row.key);
    try {
      if (row.hasPage) await (kind === 'archive' ? archiveProject : restoreProject)(row.projectId);
      else await (kind === 'archive' ? archiveCostsOnlyJob : restoreCostsOnlyJob)(row.job);
      await loadJobs();
      setError(null);
    } catch (e) {
      setError(e.message || `Failed to ${kind} the job`);
    } finally {
      setBusy(null);
    }
  };
  const menuOf = (row) => {
    const items = [{ label: row.hasPage ? 'Open project' : 'Open in Job Costs', onClick: () => openRow(row) }];
    if (canArchive) items.push({ divider: true }, row.archived ? { label: 'Restore', onClick: () => act(row, 'restore') } : { label: 'Archive job', onClick: () => act(row, 'archive') });
    return items;
  };

  const m = ar;
  const monthName = billing ? new Date(`${billing.ym}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
  const pctOf = (a, b) => (b > 0 ? (a / b) * 100 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .acct-row:hover { background: color-mix(in oklch, var(--bg-hover) 55%, transparent); }
        .acct-row:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
        .acct-num { text-align: right; font-size: 13.5px; color: var(--text-1); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .acct-total { font-weight: 700; }
        @media (max-width: 760px) { .acct-top { grid-template-columns: minmax(0, 1fr) !important; } }
      `}</style>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="acct-top" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <SummaryCard
          title="Money to collect"
          empty={m === undefined ? 'Loading…' : m === null ? 'AR isn’t available right now.' : null}
          left={m && { value: formatMoneyCompact(m.totals.net), sub: `open AR · ${m.totals.invoices} invoice${m.totals.invoices === 1 ? '' : 's'}` }}
          right={m && { value: formatMoneyCompact(m.totals.overdue), sub: `past due · ${m.totals.pctPastDue.toFixed(1)}%`, color: m.totals.overdue > 0 ? 'var(--warning)' : undefined }}
          bar={m && m.buckets.map((b, i) => ({ key: b.key, title: `${b.label}: ${formatMoney(b.amount)}`, pct: pctOf(b.amount, m.totals.net), tone: ['var(--success)', 'var(--warning)', 'var(--danger)'][i] }))}
          foot={m && `${m.buckets.map((b) => `${b.label.replace(' past due', '')} ${formatMoneyCompact(b.amount)}`).join(' · ')}${m.avgDays != null ? ` · avg ${m.avgDays} days to pay` : ''}`}
        />
        <SummaryCard
          title={`Billing this month${monthName ? ` · ${monthName}` : ''}`}
          empty={billing === undefined ? 'Loading…' : billing === null ? 'Billing isn’t available right now.' : null}
          left={billing && { value: billing.billed == null ? '—' : formatMoneyCompact(billing.billed), sub: `billed in CE · ${billing.invoices} invoice${billing.invoices === 1 ? '' : 's'}` }}
          right={billing && billing.projected != null && { value: formatMoneyCompact(billing.projected), sub: 'projected', color: 'var(--text-2)' }}
          bar={billing && billing.projected ? [{ key: 'b', title: 'Billed of projected', pct: pctOf(billing.billed || 0, billing.projected), tone: 'var(--accent)' }] : null}
          foot={billing && [
            billing.projected ? `${Math.round(pctOf(billing.billed || 0, billing.projected))}% of projected` : 'nothing projected this month',
            billing.pending ? `${formatMoneyCompact(billing.pending)} pending review` : null,
            billing.notBilled ? `${billing.notBilled} projected job${billing.notBilled === 1 ? '' : 's'} not billed yet` : null,
          ].filter(Boolean).join(' · ')}
        />
      </div>

      <section style={{ ...card, padding: 0, overflow: 'hidden' }} aria-label="Every job">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{tab === 'archived' ? 'Archived jobs' : 'Active jobs'}</h2>
          <SegmentedControl value={tab} onChange={setTab} options={[
            { value: 'active', label: 'Active', count: rows ? counts.active : undefined },
            { value: 'archived', label: 'Archived', count: rows ? counts.archived : undefined },
          ]} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 10, padding: '8px 25px', borderTop: '1px solid var(--border-subtle)', ...label, fontSize: 11 }}>
          <span /><span>Job</span><span style={{ textAlign: 'right' }}>Contract</span><span style={{ textAlign: 'right' }}>Billed</span><span style={{ textAlign: 'right' }}>Collected</span><span style={{ textAlign: 'right' }}>Left to bill</span><span />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 12px 14px' }}>
          {!rows ? (
            <div style={{ ...subText, padding: 16 }}>Loading jobs…</div>
          ) : groups.length === 0 ? (
            search.trim() ? <StateScreen kind="empty" {...noMatchText(search)} />
              : <StateScreen kind="empty" title={tab === 'archived' ? 'No archived jobs' : 'No active jobs'} body={tab === 'archived' ? 'A job someone archives shows here, under its year or its type.' : 'Every active job shows here, under its year or its type.'} />
          ) : groups.map((g) => (
            <JobGroup key={`${tab}-${g.key}`} title={g.title} note={g.note} rows={g.rows} defaultOpen={!!search.trim()}>
              {g.rows.map((r) => <JobRow key={r.key} row={r} onOpen={openRow} menuItems={menuOf(r)} busy={busy === r.key} />)}
            </JobGroup>
          ))}
        </div>
      </section>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default AccountingDashboard;
